// Libs
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { Controller, DefaultValues, FieldValues, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import type { AnyObjectSchema } from 'yup';
// Components, Layouts, Pages
import { BaseSelect, Loading } from '~/components/common';
// Others
import { IBaseOption } from '~/utils/interface/common';
// Styles, images, icons

type FieldConfig<T extends FieldValues, O extends IBaseOption = IBaseOption> = {
  name: keyof T;
  label: string;
  options: O[];
  required?: boolean;
  objectFieldName?: keyof T;
};

type Props<T extends FieldValues, O extends IBaseOption = IBaseOption> = {
  title: string;
  defaultValues: DefaultValues<T>;
  fields: FieldConfig<T, O>[];
  schema: AnyObjectSchema;
  onClose: () => void;
  autoSaveObjects?: boolean;
  onSelectOption?: (data: string, dataSelected: O) => void;
  onSubmit: (data: T) => Promise<void> | void;
  textCancel?: string;
  textSubmit?: string;
};

const ChangeValueModal = <T extends FieldValues, O extends IBaseOption = IBaseOption>(props: Props<T, O>) => {
  //#region Destructuring Props
  const {
    title,
    onClose,
    defaultValues,
    fields,
    onSubmit,
    schema,
    onSelectOption,
    autoSaveObjects = false,
    textCancel,
    textSubmit,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<T>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!defaultValues) {
      reset();
      return;
    }

    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleClose = () => {
    onClose && onClose();
    reset(defaultValues);
  };

  const handleSubmitForm = async (data: T) => {
    setIsLoading(true);
    try {
      await onSubmit(data);
      handleClose();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (field: FieldConfig<T>, option: O) => {
    onSelectOption && onSelectOption(option.value, option);
    const shouldSaveObject = field.objectFieldName || autoSaveObjects;

    if (shouldSaveObject) {
      setValue(field.objectFieldName as any, option as any, { shouldDirty: true });
    }
  };
  //#endregion Handle Function

  return (
    <Dialog id='changeValueModalComponent' open={true} onClose={handleClose} fullWidth maxWidth={'xs'}>
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitForm)}>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            {fields.map((field) => (
              <Grid2 size={12} key={String(field.name)}>
                <Controller
                  name={field.name as any}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t(field.label)}
                      value={value}
                      options={field.options}
                      onChange={(values) => {
                        handleSelectChange(field, values);
                        onChange(values.value);
                      }}
                      errorMessage={errors[field.name]?.message as string}
                      required={field.required}
                    />
                  )}
                />
              </Grid2>
            ))}
          </Grid2>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleClose}>
            {textCancel ? textCancel : t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={!isDirty}>
            {textSubmit ? textSubmit : t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default ChangeValueModal;
